import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { protocolsI18n } from './i18n';
import {
  analyzeProtocolCalls,
  getProtocolsGroupedByBusinessLine,
  getProtocolLogs,
  getProtocolSteps,
  getAgentCallList,
} from '../../services/protocolService';
import protocolDummyData from './protocolMockup';
import { getCurrentUser } from '../../services/authService';
import { useLocation, useNavigate } from 'react-router-dom';
import { getUserVenta } from '../../services/usersServices';
import NewAnalysisModal from './NewAnalysisModal';
import './protocol.scss';
import usePermissionsAI from 'permissions/modules/artificialIntelligence/usePermissionsAI';
const Protocols = ({ DesignSystemComponents }) => {
  const { ComboBox, Card, Button, Table, Modal, IconButton, Input } =
    DesignSystemComponents || {};
  const navigate = useNavigate();
  const location = useLocation();
  const logProtocolIdToOpen =
    location.state?.logProtocolId ?? location.state?.logprotocolid ?? null;

  const { language } = useLanguage();
  const prLang = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: protocolsI18n });
  useEffect(() => {
    protocolsI18n.changeLanguage(prLang);
  }, [prLang]);

  const [protocolOptions, setProtocolOptions] = useState([]);
  const [selectedProtocol, setSelectedProtocol] = useState(null);

  const [protocolSteps, setProtocolSteps] = useState([]);

  const [selectedAgent, setSelectedAgent] = useState(null);
  const [remoteAgents, setRemoteAgents] = useState([]);
  const [selectedCallsCount, setSelectedCallsCount] = useState(0);
  const [isLoadingCallsCount, setIsLoadingCallsCount] = useState(false);
  const [agentCallList, setAgentCallList] = useState([]);
  const [selectedCallIds, setSelectedCallIds] = useState([]);
  const [analysisRows, setAnalysisRows] = useState([]);
  const [analysisPage, setAnalysisPage] = useState(0);
  const [analysisPageSize, setAnalysisPageSize] = useState(10);
  const [analysisSortKey, setAnalysisSortKey] = useState(null);
  const [analysisSortDirection, setAnalysisSortDirection] = useState(null);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [analysisTotal, setAnalysisTotal] = useState(0);
  const [analysisSearchInput, setAnalysisSearchInput] = useState('');
  const [analysisSearch, setAnalysisSearch] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [modalStep, setModalStep] = useState('form');

  const analysisTypeOptions = useMemo(
    () => [
      { label: t('pr_analysis_type_individual'), value: 'individual' },
      { label: t('pr_analysis_type_group'), value: 'group' },
    ],
    [t]
  );
  const [analysisType, setAnalysisType] = useState(null);
  const effectiveAnalysisType = analysisType ?? analysisTypeOptions[0];

  const [selectedGroupAgents, setSelectedGroupAgents] = useState([]);

  const { canCreateAnalysis, canViewAnalysisDetail } = usePermissionsAI();

  const toInputDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseInputDate = (value) => {
    if (!value || typeof value !== 'string') return null;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const [selectedDate, setSelectedDate] = useState(() =>
    toInputDate(new Date())
  );

  // Load protocol list once
  useEffect(() => {
    const loadProtocols = async () => {
      try {
        const response = await getProtocolsGroupedByBusinessLine();

        const groups = response?.data ?? [];
        const flattened = groups.flatMap((group) =>
          (group.protocols || []).map((p) => ({
            label: p.protocolName,
            value: p.protocolId,
            businessLineName: group.businessLineName,
          }))
        );

        setProtocolOptions(flattened);
      } catch (error) {
        console.error('Error loading protocols:', error);
      }
    };

    loadProtocols();
  }, []);

  // When protocol changes, load steps
  useEffect(() => {
    if (!selectedProtocol) {
      setProtocolSteps([]);
      return;
    }

    const loadDataForProtocol = async () => {
      try {
        const stepsResponse = await getProtocolSteps(selectedProtocol.value);

        const steps = Array.isArray(stepsResponse) ? stepsResponse : [];
        setProtocolSteps(steps);
      } catch (error) {
        console.error('Error loading protocol steps:', error);
        setProtocolSteps([]);
      }
    };

    loadDataForProtocol();
  }, [selectedProtocol]);

  useEffect(() => {
    setSelectedAgent(null);
  }, [selectedProtocol]);

  useEffect(() => {
    if (effectiveAnalysisType?.value !== 'group') return;
    setSelectedAgent(null);
    setSelectedCallsCount(0);
    setSelectedCallIds([]);
  }, [effectiveAnalysisType]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setAnalysisSearch(analysisSearchInput);
      setAnalysisPage(0);
    }, 300);

    return () => clearTimeout(timeout);
  }, [analysisSearchInput]);

  useEffect(() => {
    if (!logProtocolIdToOpen) return;
    const idStr = String(logProtocolIdToOpen);
    setAnalysisSearchInput(idStr);
    setAnalysisSearch(idStr);
    setAnalysisPage(0);
  }, [logProtocolIdToOpen]);

  // Derived dummy data based on selected protocol's business line
  const currentBusinessLineData = selectedProtocol
    ? protocolDummyData.find(
        (bl) => bl.businessLineName === selectedProtocol.businessLineName
      )
    : null;

  const allAgents = currentBusinessLineData?.agents ?? [];
  const fallbackAgentOptions = allAgents.map((a) => ({
    label: `${a.firstName} ${a.lastName}`,
    value: a.id,
    role: a.role,
    specialty: a.specialty || currentBusinessLineData?.businessLineName || '',
  }));

  const agentOptions = remoteAgents.length
    ? remoteAgents
    : fallbackAgentOptions;

  const groupAgentRows = useMemo(
    () =>
      remoteAgents.map((agent) => ({
        id: agent.value,
        name: agent.label,
        email: agent.email || '--',
      })),
    [remoteAgents]
  );

  const toggleGroupAgent = (agentId) => {
    setSelectedGroupAgents((prev) =>
      prev.includes(agentId)
        ? prev.filter((item) => item !== agentId)
        : [...prev, agentId]
    );
  };

  const toggleGroupAll = (targetIds) => {
    const ids =
      Array.isArray(targetIds) && targetIds.length
        ? targetIds
        : groupAgentRows.map((row) => row.id);
    if (ids.length === 0) return;
    const allSelected = ids.every((id) => selectedGroupAgents.includes(id));
    setSelectedGroupAgents((prev) => {
      if (allSelected) {
        return prev.filter((id) => !ids.includes(id));
      }
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const toggleCallId = (callId) => {
    if (callId === null || callId === undefined) return;
    setSelectedCallIds((prev) =>
      prev.includes(callId)
        ? prev.filter((id) => id !== callId)
        : [...prev, callId]
    );
  };

  const toggleCallAll = (targetIds) => {
    const ids =
      Array.isArray(targetIds) && targetIds.length
        ? targetIds
        : agentCallList.map((call) => call.callId);
    if (ids.length === 0) return;
    const allSelected = ids.every((id) => selectedCallIds.includes(id));
    setSelectedCallIds((prev) => {
      if (allSelected) {
        return prev.filter((id) => !ids.includes(id));
      }
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const normalizeUserVenta = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    return [];
  };

  useEffect(() => {
    const controller = new AbortController();

    const loadAgents = async () => {
      try {
        const response = await getUserVenta(controller.signal);
        const agents = normalizeUserVenta(response);
        const mapped = agents
          .map((agent) => {
            const agentId = agent?.agentId;
            if (agentId === null || agentId === undefined) return null;
            const label = agent?.name || agent?.email || t('pr_fallback_agent');
            return {
              label,
              value: agentId,
              userId: agent?.id ?? agent?.userId ?? null,
              email: agent?.email ?? null,
            };
          })
          .filter(Boolean);

        setRemoteAgents(mapped);
      } catch (error) {
        console.error('Error loading agents:', error);
        setRemoteAgents([]);
      }
    };

    loadAgents();

    return () => controller.abort();
  }, [t]);

  const dateRangeBounds = useMemo(() => {
    const baseDate = parseInputDate(selectedDate);
    if (!baseDate) return null;
    const start = new Date(baseDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(baseDate);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }, [selectedDate]);

  const toISO = (date) =>
    new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();

  useEffect(() => {
    const controller = new AbortController();

    const loadCallList = async () => {
      if (!selectedAgent || !selectedDate) {
        setSelectedCallsCount(0);
        setAgentCallList([]);
        setIsLoadingCallsCount(false);
        return;
      }

      const agentId = Number(selectedAgent.value);
      if (Number.isNaN(agentId)) {
        setSelectedCallsCount(0);
        setAgentCallList([]);
        setIsLoadingCallsCount(false);
        return;
      }

      try {
        setIsLoadingCallsCount(true);
        const day = selectedDate;
        const response = await getAgentCallList(
          { agentId, day },
          controller.signal
        );
        const calls = Array.isArray(response?.calls)
          ? response.calls
          : Array.isArray(response?.data?.calls)
            ? response.data.calls
            : Array.isArray(response?.data)
              ? response.data
              : [];
        const count = response?.count ?? response?.total ?? calls.length ?? 0;
        setSelectedCallsCount(Number(count) || 0);
        setAgentCallList(calls);
      } catch (error) {
        console.error('Error loading calls list:', error);
        setSelectedCallsCount(0);
        setAgentCallList([]);
      } finally {
        setIsLoadingCallsCount(false);
      }
    };

    loadCallList();

    return () => controller.abort();
  }, [selectedAgent, selectedDate]);

  useEffect(() => {
    if (!agentCallList.length) {
      setSelectedCallIds([]);
      return;
    }
    setSelectedCallIds((prev) =>
      prev.filter((id) => agentCallList.some((call) => call.callId === id))
    );
  }, [agentCallList]);

  const analysisPayload = useMemo(() => {
    if (!selectedProtocol || !selectedAgent || !dateRangeBounds) return null;

    const currentUser = getCurrentUser?.();
    const supervisorUserId =
      currentUser?.userId ?? currentUser?.id ?? currentUser?.userID ?? null;

    return {
      protocolId: selectedProtocol.value,
      agentId: String(selectedAgent.userId ?? selectedAgent.value),
      transcriptionIds: selectedCallIds,
      supervisorUserId,
      startDate: toISO(dateRangeBounds.start),
      endDate: toISO(dateRangeBounds.end),
    };
  }, [selectedProtocol, selectedAgent, dateRangeBounds, selectedCallIds]);

  const isGroupAnalysis = effectiveAnalysisType?.value === 'group';

  const canAnalyze = Boolean(
    !isGroupAnalysis &&
      analysisPayload &&
      !isAnalyzing &&
      selectedCallIds.length > 0
  );

  const handleOpenConfirm = () => {
    if (!analysisPayload) return;
    setModalStep('confirm');
  };

  const formatDate = (value) => {
    if (!value) return '';
    const parsedInput = parseInputDate(value);
    const d = parsedInput || new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return `${String(d.getDate()).padStart(2, '0')}/${String(
      d.getMonth() + 1
    ).padStart(2, '0')}/${d.getFullYear()}`;
  };

  const formatDateTime = (value) => {
    if (!value) return 'N/A';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const coalesce = (...values) =>
    values.find(
      (value) => value !== null && value !== undefined && value !== ''
    );

  const normalizeProtocolLogs = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.logs)) return payload.logs;
    return [];
  };

  const formatLogDate = (log) => {
    const from = coalesce(log.fromDate);
    const to = coalesce(log.toDate);

    if (from || to) {
      const fromText = from ? formatDate(from) : '';
      const toText = to ? formatDate(to) : '';
      return fromText && toText
        ? `${fromText} - ${toText}`
        : fromText || toText;
    }

    const single = coalesce(log.createdAt);

    return single ? formatDate(single) : '';
  };

  const formatOverallScore = (value) => {
    if (value === null || value === undefined || value === '') return '--';
    const cleaned =
      typeof value === 'string' ? value.replace(/[^\d.-]/g, '') : value;
    const asNumber = Number(cleaned);
    if (Number.isNaN(asNumber)) return String(value);
    return `${Math.floor(asNumber)}%`;
  };

  const mapProtocolLogRow = (log) => {
    const overallScoreValue = coalesce(log.percent);

    return {
      id: coalesce(log.id),
      user: coalesce(log.userName),
      agent: coalesce(log.agentName),
      date: formatLogDate(log),
      protocol: coalesce(log.protocolName),
      calls: coalesce(log.calls),
      overallScore: formatOverallScore(overallScoreValue),
      load: log.load,
    };
  };

  const mapSortKeyToBackend = (key) => {
    switch (key) {
      case 'user':
        return 'userName';
      case 'agent':
        return 'agentName';
      case 'date':
        return 'createdAt';
      case 'protocol':
        return 'protocolName';
      case 'calls':
        return 'calls';
      case 'overallScore':
        return 'percent';
      default:
        return null;
    }
  };

  const loadProtocolLogs = async ({ signal, page } = {}) => {
    try {
      setIsLoadingLogs(true);
      const pageToUse =
        typeof page === 'number' && Number.isFinite(page) ? page : analysisPage;
      const backendSortKey = mapSortKeyToBackend(analysisSortKey);
      const backendSortDesc = backendSortKey
        ? analysisSortDirection === null
          ? true
          : String(analysisSortDirection) === 'desc'
        : null;
      const response = await getProtocolLogs(
        pageToUse + 1,
        analysisPageSize,
        backendSortKey,
        backendSortDesc,
        analysisSearch,
        signal
      );
      const logs = normalizeProtocolLogs(response);
      const rows = logs.map(mapProtocolLogRow);
      setAnalysisRows(rows);
      setAnalysisTotal(response?.total ?? 0);
      return rows;
    } catch (error) {
      console.error('Error loading protocol logs:', error);
      return null;
    } finally {
      setIsLoadingLogs(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    loadProtocolLogs({ signal: controller.signal });
    return () => controller.abort();
  }, [
    analysisPage,
    analysisPageSize,
    analysisSortKey,
    analysisSortDirection,
    analysisSearch,
    refreshKey,
  ]);

  const handleOpenCreateModal = () => {
    setModalStep('form');
    setAnalysisType(analysisTypeOptions[0]);
    setSelectedGroupAgents([]);
    setShowCreateModal(true);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setModalStep('form');
  };

  const handleViewDetail = (row) => {
    if (!row?.id) return;
    navigate(`/ia/protocolos/detalle-general?id=${row.id}`);
  };

  const analysisColumns = [
    {
      accessor: 'user',
      label: t('pr_col_user'),
      sort: true,
    },
    {
      accessor: 'agent',
      label: t('pr_col_agent'),
      sort: true,
    },
    {
      accessor: 'date',
      label: t('pr_col_date'),
      sort: true,
    },
    {
      accessor: 'protocol',
      label: t('pr_col_protocol'),
      sort: true,
    },
    {
      accessor: 'calls',
      label: t('pr_col_calls'),
      sort: true,
    },
    {
      accessor: 'overallScore',
      label: t('pr_col_result'),
      sort: true,
    },
    {
      accessor: 'action',
      render: (row) =>
        row?.load === false ? (
          <span
            className='spinner-border spinner-border-sm text-primary'
            role='status'
            aria-label={t('pr_loading')}
          />
        ) : IconButton ? (
          <IconButton
            ariaLabel={t('pr_btn_view')}
            icon='eye'
            size='md'
            onClick={() => handleViewDetail(row)}
            disabled={!canViewAnalysisDetail}
          />
        ) : Button ? (
          <Button
            label={t('pr_btn_view')}
            size='sm'
            variant='secondary'
            onClick={() => handleViewDetail(row)}
            disabled={!canViewAnalysisDetail}
          />
        ) : (
          <button
            type='button'
            className='btn btn-secondary btn-sm'
            onClick={() => handleViewDetail(row)}
            disabled={!canViewAnalysisDetail}
          >
            {t('pr_btn_view')}
          </button>
        ),
    },
  ];

  const handleServerSortChange = ({ sortKey, direction }) => {
    const normalized =
      typeof direction === 'string' ? direction.toLowerCase() : direction;
    setAnalysisSortKey(normalized ? sortKey : null);
    setAnalysisSortDirection(normalized ?? null);
    setAnalysisPage(0);
  };

  const handlePageSizeChange = (newSize) => {
    setAnalysisPageSize(newSize);
    setAnalysisPage(0);
  };

  const handleAnalyzeRequest = async () => {
    if (!analysisPayload) return;
    setIsAnalyzing(true);
    setShowCreateModal(false);
    setModalStep('form');
    try {
      await analyzeProtocolCalls(analysisPayload);
      setAnalysisPage(0);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error('Error analyzing protocol calls:', error);
    } finally {
      setIsAnalyzing(false);
      setModalStep('form');
    }
  };

  return (
    <div className='protocols-page'>
      <div className='mb-4 d-flex justify-content-between align-items-start'>
        <div>
          <h2 className='fw-semibold'>{t('pr_page_title')}</h2>
          <h4 className='fw-normal'>{t('pr_page_subtitle')}</h4>
        </div>
        {Button && (
          <div className='d-flex gap-2'>
            {Modal && (
              <Button
                label={t('pr_btn_new_analysis')}
                variant='primary'
                size='sm'
                onClick={handleOpenCreateModal}
                disabled={!canCreateAnalysis}
              />
            )}
          </div>
        )}
      </div>

      <Card specialPadding='px-4 pt-4 pb-2'>
        <div className='mb-3'>
          {Input ? (
            <Input
              icon='search'
              name='searchProtocol'
              onChange={(e) => {
                setAnalysisSearchInput(e.target.value);
              }}
              placeholder={t('pr_search_placeholder')}
              type='text'
              value={analysisSearchInput}
            />
          ) : (
            <input
              className='form-control'
              placeholder={t('pr_search_placeholder')}
              type='text'
              value={analysisSearchInput}
              onChange={(e) => {
                setAnalysisSearchInput(e.target.value);
              }}
            />
          )}
        </div>
        <div className='table-container log-protocol-table'>
          {Table ? (
            <Table
              columns={analysisColumns}
              data={analysisRows}
              page={analysisPage}
              onPageChange={setAnalysisPage}
              pageSize={analysisPageSize}
              onPageSizeChange={handlePageSizeChange}
              paginationMode='server'
              totalItems={analysisTotal}
              paginator
              paginatorGroup={[
                { label: '10', value: 10 },
                { label: '20', value: 20 },
                { label: '50', value: 50 },
                { label: '100', value: 100 },
              ]}
              serverSortKey={analysisSortKey}
              serverSortDirection={analysisSortDirection}
              onServerSortChange={handleServerSortChange}
              loading={isLoadingLogs}
            />
          ) : (
            <p className='color-gray-600 small mb-0'>
              {t('pr_table_unavailable')}
            </p>
          )}
        </div>
      </Card>

      <NewAnalysisModal
        Modal={Modal}
        Button={Button}
        ComboBox={ComboBox}
        Input={Input}
        Table={Table}
        show={showCreateModal}
        onClose={handleCloseModal}
        modalStep={modalStep}
        onConfirm={handleOpenConfirm}
        onBack={() => setModalStep('form')}
        onAnalyze={handleAnalyzeRequest}
        isAnalyzing={isAnalyzing}
        canAnalyze={canAnalyze}
        protocolOptions={protocolOptions}
        selectedProtocol={selectedProtocol}
        onSelectProtocol={setSelectedProtocol}
        protocolSteps={protocolSteps}
        agentOptions={agentOptions}
        selectedAgent={selectedAgent}
        onSelectAgent={setSelectedAgent}
        selectedDate={selectedDate}
        onSelectedDateChange={setSelectedDate}
        callListRows={agentCallList}
        selectedCallIds={selectedCallIds}
        onToggleCallId={toggleCallId}
        onToggleCallAll={toggleCallAll}
        selectedCallsCount={selectedCallsCount}
        isLoadingCallsCount={isLoadingCallsCount}
        formatDate={formatDate}
        formatDateTime={formatDateTime}
        analysisType={effectiveAnalysisType}
        analysisTypeOptions={analysisTypeOptions}
        onAnalysisTypeChange={setAnalysisType}
        groupRows={groupAgentRows}
        selectedGroupAgents={selectedGroupAgents}
        onToggleGroupAgent={toggleGroupAgent}
        onToggleGroupAll={toggleGroupAll}
      />
    </div>
  );
};

export default Protocols;
